import type { TreeNode, ViewMode } from '@mona-roadmap/domain';
import { readJson, writeJson } from './storage';

/**
 * Expansion state is stored as explicit overrides (node.key -> open?). Anything without an override
 * falls back to the default: top-level nodes (goals / epics in the epic view) open, deeper nodes closed.
 * Keys are stable per occurrence, so state survives edits, refetches and view toggles.
 */
export type ExpansionOverrides = Record<string, boolean>;

export const defaultExpanded = (node: TreeNode): boolean => node.depth === 0;

/** `openByDefault` (used while filters are active) opens every level so matches are visible. */
export function computeExpanded(roots: TreeNode[], overrides: ExpansionOverrides, openByDefault = false): Set<string> {
  const open = new Set<string>();
  const walk = (nodes: TreeNode[]) => {
    for (const n of nodes) {
      if (n.children.length === 0) continue;
      if (overrides[n.key] ?? (openByDefault || defaultExpanded(n))) open.add(n.key);
      walk(n.children);
    }
  };
  walk(roots);
  return open;
}

export function setAll(keys: string[], value: boolean, base: ExpansionOverrides = {}): ExpansionOverrides {
  const next = { ...base };
  for (const k of keys) next[k] = value;
  return next;
}

export const expansionStorageKey = (view: ViewMode): string => `mona.expanded.${view}`;
export const loadExpansion = (view: ViewMode): ExpansionOverrides => readJson<ExpansionOverrides>(expansionStorageKey(view), {});
export const saveExpansion = (view: ViewMode, o: ExpansionOverrides): void => writeJson(expansionStorageKey(view), o);
